/**
 * NudgeModal (Iter 34) — fire a one-click reminder email to a share recipient.
 *
 * Used from two places:
 *   - HeatmapSharesList (any active share with a recipient_email)
 *   - WeeklyDigestCard chase-candidate rows
 *
 * Renders a small modal with the auto-filled subject + a textarea for an
 * optional personal note. The actual email body is rendered server-side via
 * `nudge_recipient_email_html` so what we show here is the *preview* of the
 * note, not the full email markup.
 *
 * Cooldown UX: if the share was nudged in the last 24h, the backend returns
 * 429 with a friendly cooldown message; we surface that as an error toast
 * with the exact remaining-hours copy.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  BellIcon as Bell,
  CircleNotchIcon as Spinner,
  WarningOctagonIcon as WarningOctagon,
} from "@phosphor-icons/react";
import { nudgeShareRecipient } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const MAX_MESSAGE = 500;

function recipientForRow(row) {
  return row.recipient_email || row.recipient_label || "";
}

function fmtTimeAgo(iso) {
  if (!iso) return "never";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return iso;
  }
}

export default function NudgeModal({
  workspaceId,
  share,            // { token, workspace_name, recipient_email, recipient_label, view_count, last_nudge_at, nudges_sent, expires_at }
  open,
  onClose,
  onSent,
}) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // Reset state every time the modal opens (different share = fresh slate).
  useEffect(() => {
    if (open) {
      setMessage("");
      setSubmitting(false);
      // Autofocus the textarea so the consultant can start typing immediately.
      setTimeout(() => textareaRef.current?.focus(), 30);
    }
  }, [open, share?.token]);

  if (!open || !share) return null;

  const recipient = recipientForRow(share);
  const hasEmail = Boolean(share.recipient_email);
  const cooldownActive =
    share.last_nudge_at &&
    Date.now() - new Date(share.last_nudge_at).getTime() < 24 * 60 * 60 * 1000;
  const recentNudgeAgo = cooldownActive ? fmtTimeAgo(share.last_nudge_at) : null;

  const handleSend = async () => {
    if (!hasEmail) {
      toast.error("This share has no recipient email — can't nudge.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await nudgeShareRecipient(workspaceId, share.token, {
        customMessage: message.trim() || undefined,
      });
      const status = res.stubbed
        ? "Nudge queued (stub mode — check backend logs)."
        : `Nudge sent to ${recipient}.`;
      toast.success(status);
      onSent?.(res);
      onClose();
    } catch (e) {
      // 429 messages include the exact remaining-hours copy from the backend.
      toast.error(formatApiErrorDetail(e) || "Failed to send nudge");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="nudge-modal-backdrop"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-lg w-full shadow-xl"
        data-testid="nudge-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div className="flex items-center gap-2">
            <Bell size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">Send a nudge</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="nudge-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!hasEmail ? (
            <div
              className="ml-card p-3 text-sm flex items-start gap-2"
              style={{ background: "#fff1f1", borderColor: "#ffd7d9", color: "#a2191f" }}
              data-testid="nudge-modal-no-email"
            >
              <WarningOctagon size={14} weight="bold" className="mt-0.5 shrink-0" />
              <span>
                This share doesn&rsquo;t have a recipient email — only a label
                ({share.recipient_label || "—"}). Mint a new share with a recipient
                email to enable nudging.
              </span>
            </div>
          ) : (
            <>
              <div className="text-sm space-y-1">
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Sending to</div>
                <div className="font-semibold" data-testid="nudge-modal-recipient">{share.recipient_email}</div>
                {share.recipient_label && (
                  <div className="text-xs text-[var(--ml-text-secondary)]">{share.recipient_label}</div>
                )}
              </div>

              <div className="text-xs text-[var(--ml-text-secondary)] flex flex-wrap gap-x-3 gap-y-0.5">
                <span><strong>{share.view_count ?? 0}</strong> views so far</span>
                {(share.nudges_sent ?? 0) > 0 && (
                  <span data-testid="nudge-modal-history">
                    Previously nudged: {share.nudges_sent} time
                    {share.nudges_sent === 1 ? "" : "s"}
                    {share.last_nudge_at ? ` · last ${fmtTimeAgo(share.last_nudge_at)}` : ""}
                  </span>
                )}
              </div>

              {cooldownActive && (
                <div
                  className="ml-card p-3 text-xs flex items-start gap-2"
                  style={{ background: "#fff8e1", borderColor: "#f1c21b", color: "#8a6c00" }}
                  data-testid="nudge-modal-cooldown-banner"
                >
                  <WarningOctagon size={12} weight="bold" className="mt-0.5 shrink-0" />
                  <span>
                    Cooldown active — this share was nudged {recentNudgeAgo}.
                    You can send another nudge after a 24-hour window. (We rate-limit
                    so the recipient never feels spammed.)
                  </span>
                </div>
              )}

              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                  Personal note <span className="opacity-50 ml-mono">(optional, max {MAX_MESSAGE} chars)</span>
                </label>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                  placeholder="Hey Jane — quick reminder about the heat map. Happy to walk you through the top three packages on a 20-min call if helpful."
                  rows={5}
                  className="ml-input w-full text-sm"
                  data-testid="nudge-modal-message"
                />
                <div className="text-right text-[10px] ml-mono text-[var(--ml-text-secondary)] mt-0.5">
                  {message.length} / {MAX_MESSAGE}
                </div>
              </div>

              <div className="text-xs text-[var(--ml-text-secondary)] ml-card p-3 bg-[var(--ml-surface)]/40">
                The email will come from your Modernization Lab account with
                <strong> Reply-To set to your address</strong>, so any reply
                lands in your inbox. Subject is <em>&ldquo;Quick reminder on the {share.workspace_name || "heat map"}&rdquo;</em>.
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="ml-btn-secondary"
              data-testid="nudge-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!hasEmail || submitting}
              data-testid="nudge-modal-send"
              className="ml-btn-primary"
              title={hasEmail ? undefined : "Add a recipient email to the share to enable nudging"}
            >
              {submitting ? (
                <><Spinner size={12} className="animate-spin" /> Sending…</>
              ) : (
                <><Bell size={12} weight="bold" /> Send nudge</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
